/**
 * girlcave — start every animated product card on the same frame.
 *
 * An animated WebP in an <img> has no start/seek/pause API: each decode begins
 * when *that* image is ready and loops on its own clock, so the product grid's
 * turntable renders drift out of phase. There is no way to align them in place.
 *
 * This works around it by taking ownership of the source. It fetches each card
 * animation once (the <img> request was already made, so this normally hits the
 * HTTP cache), confirms the bytes really are an animated WebP by looking for
 * the `ANIM`/`ANMF` chunks — not every `.webp` media file animates — and then
 * keeps the ArrayBuffer. On every (debounced) settle it mints fresh `blob:`
 * URLs for the whole set and assigns them in one synchronous pass, so every
 * decoder is handed its source in the same task and the loops begin together.
 * Because the renders share the same frame timing the group then stays in sync.
 *
 * A fresh blob URL per sync is deliberate: reassigning the *same* URL would be
 * a no-op and never restart the animation.
 *
 * Scope: the product grid only. The PDP shows a single animation, so there is
 * nothing to align. Lazy cards below the fold join on the next settle, which
 * restarts the whole visible set together.
 *
 * The CDN content-negotiates animated media — a request without an
 * `image/webp` hint is served a GIF fallback — so the fetch asks for WebP
 * explicitly (same hint the browser sends for the <img>).
 *
 * Mobile speed (2026-09-24, user request: "make the loop faster on mobile").
 * Frame duration is baked into each ANMF chunk of the file, so an <img> cannot
 * be sped up by CSS. Since the bytes are already in hand, the same pass can
 * patch the 3-byte duration field of every ANMF chunk before building the blob
 * — a byte-level retime, no re-encode. The multiplier comes from the grid's
 * `data-gc-mobile-speed` (section setting, default 2x) and applies only below
 * 768px; desktop is left at the file's own timing. Crossing the breakpoint
 * re-syncs, which rebuilds the blobs at the new speed.
 */
(function () {
  'use strict';

  var GRID = '[data-gc-grid]';
  var CARD_IMG = '.gc-card__image img';
  var SETTLE_MS = 200;

  var cache = new WeakMap(); // img -> { url, buffer, animated, blobUrl }
  var timers = new WeakMap();
  var watched = new WeakSet(); // grids with listeners attached
  var blobbed = new Set(); // imgs currently showing a blob URL
  var mobileMq = window.matchMedia('(max-width: 767px)');
  var MIN_FRAME_MS = 10; // floor, so a fast multiplier can't stall a decoder

  function isAnimatedWebp(buffer) {
    var d = new Uint8Array(buffer);
    if (d.length < 16) return false;
    // "RIFF" .... "WEBP"
    if (d[0] !== 0x52 || d[1] !== 0x49 || d[2] !== 0x46 || d[3] !== 0x46) return false;
    if (d[8] !== 0x57 || d[9] !== 0x45 || d[10] !== 0x42 || d[11] !== 0x50) return false;
    var view = new DataView(buffer);
    var i = 12;
    while (i + 8 <= d.length) {
      var tag = String.fromCharCode(d[i], d[i + 1], d[i + 2], d[i + 3]);
      var size = view.getUint32(i + 4, true);
      if (tag === 'ANIM' || tag === 'ANMF') return true;
      i += 8 + size + (size & 1);
      if (size < 0) break;
    }
    return false;
  }

  function mobileSpeed(grid) {
    var n = parseFloat(grid.getAttribute('data-gc-mobile-speed'));
    return mobileMq.matches && n > 0 ? n : 1;
  }

  // Byte-level retime: overwrite each ANMF frame's 3-byte duration (ms, at
  // payload + 12) with dur / speed. Returns the original buffer at speed 1.
  function retime(buffer, speed) {
    if (speed === 1) return buffer;
    var out = new Uint8Array(buffer).slice();
    var view = new DataView(out.buffer);
    var i = 12;
    while (i + 8 <= out.length) {
      var size = view.getUint32(i + 4, true);
      if (out[i] === 0x41 && out[i + 1] === 0x4e && out[i + 2] === 0x4d && out[i + 3] === 0x46) {
        // "ANMF"
        var p = i + 8 + 12;
        var dur = out[p] | (out[p + 1] << 8) | (out[p + 2] << 16);
        var next = Math.max(MIN_FRAME_MS, Math.round(dur / speed));
        out[p] = next & 0xff;
        out[p + 1] = (next >> 8) & 0xff;
        out[p + 2] = (next >> 16) & 0xff;
      }
      i += 8 + size + (size & 1);
      if (size < 0) break;
    }
    return out.buffer;
  }

  function load(img) {
    var url = img.currentSrc || img.src;
    if (!url) return Promise.resolve(null);
    if (url.indexOf('blob:') === 0) return Promise.resolve(cache.get(img) || null);
    var hit = cache.get(img);
    if (hit && hit.url === url) return Promise.resolve(hit);
    return fetch(url, { headers: { Accept: 'image/webp,image/*,*/*' } })
      .then(function (res) {
        if (!res.ok) return null;
        return res.arrayBuffer();
      })
      .then(function (buffer) {
        if (!buffer) return null;
        var entry = { url: url, buffer: buffer, animated: isAnimatedWebp(buffer), blobUrl: null };
        cache.set(img, entry);
        return entry;
      })
      .catch(function () {
        return null;
      });
  }

  function sync(grid) {
    var imgs = Array.prototype.slice.call(grid.querySelectorAll(CARD_IMG));
    Promise.all(
      imgs.map(function (img) {
        return load(img);
      })
    ).then(function (entries) {
      var animated = [];
      imgs.forEach(function (img, i) {
        if (entries[i] && entries[i].animated) animated.push(img);
      });
      if (!animated.length) return;
      var speed = mobileSpeed(grid);
      // One synchronous pass: every decoder is handed its source in this task,
      // so the loops start on the same frame.
      animated.forEach(function (img) {
        var entry = cache.get(img);
        if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl);
        entry.blobUrl = URL.createObjectURL(new Blob([retime(entry.buffer, speed)], { type: 'image/webp' }));
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = entry.blobUrl;
        blobbed.add(img);
      });
    });
  }

  function schedule(grid) {
    var t = timers.get(grid);
    if (t) clearTimeout(t);
    timers.set(
      grid,
      setTimeout(function () {
        sync(grid);
      }, SETTLE_MS)
    );
  }

  function init() {
    var grids = document.querySelectorAll(GRID);
    Array.prototype.forEach.call(grids, function (grid) {
      if (watched.has(grid)) return;
      watched.add(grid);
      // Images arrive progressively (eager now, lazy on scroll, load-more later).
      // Every settle restarts the whole set, so the group never drifts.
      grid.addEventListener(
        'load',
        function (e) {
          var target = e.target;
          // Ignore the load our own blob swap just fired — otherwise it would
          // schedule another sync, forever.
          if (target && (target.currentSrc || '').indexOf('blob:') === 0) return;
          schedule(grid);
        },
        true
      );
      new MutationObserver(function () {
        schedule(grid);
      }).observe(grid, { childList: true, subtree: true });
      schedule(grid);
    });
  }

  // Crossing the mobile breakpoint changes the playback speed (and with it
  // the blob), so rebuild the set.
  var reflow = function () {
    Array.prototype.forEach.call(document.querySelectorAll(GRID), schedule);
  };
  if (mobileMq.addEventListener) mobileMq.addEventListener('change', reflow);
  else if (mobileMq.addListener) mobileMq.addListener(reflow);

  window.addEventListener('pagehide', function () {
    blobbed.forEach(function (img) {
      var entry = cache.get(img);
      if (entry && entry.blobUrl) URL.revokeObjectURL(entry.blobUrl);
    });
  });

  // Soft page changes (assets/gc-navigation.js): a swapped-in grid needs its
  // own watchers, and the blobs of a grid that left the DOM are released —
  // a blob URL pins its buffer until revoked, and each home-page visit would
  // otherwise leave a full set of animations behind.
  document.addEventListener('gc:page-load', function () {
    blobbed.forEach(function (img) {
      if (img.isConnected) return;
      var entry = cache.get(img);
      if (entry && entry.blobUrl) URL.revokeObjectURL(entry.blobUrl);
      blobbed.delete(img);
    });
    init();
  });

  // Also when this file itself arrives after DOMContentLoaded (injected by a
  // soft page change).
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();